import { cloneDeep } from 'lodash';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, Input, OnDestroy, SecurityContext } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';

import { ChatMediaMessageDescriptionDialogComponent } from '../../components/chat-media-message-description-dialog/chat-media-message-description-dialog.component';
import { SnChannelType } from '../../enums';
import { SnChannel } from '../../models';
import { SnChatService, SnMessagesService } from '../../services';

@Component({
  selector: 'sn-new-message',
  templateUrl: './new-message.container.html',
  styleUrls: ['./new-message.container.scss'],
})
export class SnNewMessageContainer implements OnDestroy {
  @Input()
  public channel: SnChannel;

  private readonly _destroy$ = new Subject();

  constructor(
    private readonly _chatService: SnChatService,
    private readonly _messagesService: SnMessagesService,
    private readonly _sanitizer: DomSanitizer,
    private readonly _dialog: MatDialog,
  ) {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public handleFiles(files: FileList): void {
    const fileList = cloneDeep(Array.from(files));
    const dialogRef = this._dialog.open(ChatMediaMessageDescriptionDialogComponent);
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        this._sendMediaMessage(fileList, result);
      });
  }

  public sendMessage(event?: KeyboardEvent): void {
    if (event) {
      event.preventDefault();
    }

    if (this.channel.currentMessage) {
      const message = this.channel.currentMessage.trim();
      const sanitizedMessage = this._sanitizer.sanitize(SecurityContext.HTML, message);

      if (sanitizedMessage) {
        this.channel.currentMessage = '';
        this._messagesService
          .sendTextMessage(this.channel, sanitizedMessage)
          .pipe(takeUntil(this._destroy$))
          .subscribe();
      }
    }
  }

  public onMessageEntering(): void {
    if (this.channel.type !== SnChannelType.Team) {
      this.channel.twilioChannel.typing();
    }
  }

  private _sendMediaMessage(fileList: File[], data: any): void {
    this._chatService.dataIsLoading = true;
    const fileStreams$ = Array.from(fileList).map((file: File) => {
      return this._messagesService.sendMediaMessage(this.channel, file, data?.description);
    });
    forkJoin(fileStreams$)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._chatService.dataIsLoading = false;
      });
  }
}
